import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSystem } from '@ohif/core';
import { usePolling } from './usePolling';
import { DiagnosisState } from '../utils';
import { DiagnosisService } from '../services/DiagnosisService';

/**
 * ONE polling owner, and one place that writes local state.
 *
 * develop had four conflicting control points: `startPolling` inside its
 * DIAGNOSIS_STATE_TRANSITION subscription handler — which fired every tick, so polling
 * was self-sustaining and ran continuously from mount regardless of state — a second
 * `startPolling` in a mount-only effect, a third effect driven by `state` that started or
 * stopped polling on every change, and a `stopPolling` inside its DIAGNOSIS_COMPLETED
 * handler. Here there is exactly one place that starts or stops it: `apply`.
 *
 * `state` below IS a mirror of the service's. Every service write broadcasts and keeps
 * it in sync through `apply`, its only writer — except `onModeExit`'s reset, which
 * clears the service's studies map without broadcasting. That gap is invisible today
 * only because nothing reads `state` during mode teardown; a future edit that keeps this
 * hook mounted across a mode exit would see the mirror diverge.
 *
 * ONE useEffect. The subscription lives inside it rather than in an effect of its own,
 * so that the polling decision can be driven by the broadcast instead of by a
 * dependency array.
 *
 * develop's hook handled ASSESSMENT_COMPLETE and ASSESSMENT_SUBMITTED with a local
 * setState that bypassed the service, so hook and service disagreed until the next
 * tick. Neither event is subscribed to here — DiagnosisService.submitAssessment moves
 * the service, and the service is what this reads.
 */
export function useDiagnosisStatus(studyInstanceUid?: string): {
  state: DiagnosisState;
  trigger: () => void;
  revoke: () => void;
} {
  const { servicesManager } = useSystem();
  const { diagnosisService } = servicesManager.services;

  // Lazily, from the service. A constant initialiser painted IDLE on every mount even
  // when the service already knew the answer — so a remount mid-run showed "Diagnose",
  // enabled, for one frame, which is the state that invites a duplicate run.
  const [state, setState] = useState<DiagnosisState>(() =>
    studyInstanceUid ? diagnosisService.getState(studyInstanceUid) : DiagnosisState.IDLE
  );

  // The guard against overlapping refresh() calls lives in DiagnosisService.refresh(),
  // where track(), the poller and submitAssessment() all converge. Guarding here would
  // only ever have covered the poller's own calls.
  const refresh = useCallback(
    () => (studyInstanceUid ? diagnosisService.refresh(studyInstanceUid) : Promise.resolve()),
    [diagnosisService, studyInstanceUid]
  );

  const { startPolling, stopPolling } = usePolling(refresh);

  useEffect(() => {
    // Mode.tsx:48 initialises studyInstanceUIDs to null and only sets it at :136, so
    // this is the normal state of the first few renders, not an error.
    if (!studyInstanceUid) {
      return undefined;
    }

    const apply = () => {
      const next = diagnosisService.getState(studyInstanceUid);
      setState(next);

      if (next === DiagnosisState.PROCESSING) {
        startPolling(refresh);
      } else {
        stopPolling();
      }
    };

    const subscriptions = [
      diagnosisService.subscribe(
        DiagnosisService.EVENTS.DIAGNOSIS_STATE_CHANGED,
        ({ studyInstanceUid: changed }) => {
          if (changed === studyInstanceUid) {
            apply();
          }
        }
      ),

      /**
       * D6. `apply` cannot cover this: a 401 does not change the state, so the study
       * stays PROCESSING and the poller keeps re-issuing it. Nor does the logout cover
       * it — SessionService.logOut awaits the log-out POST before its finally redirects,
       * and HttpClient's default timeout is 15s, so three more 401s land first; and if
       * getLoginUrl() calls fatal() there is no redirect at all.
       */
      diagnosisService.subscribe(
        DiagnosisService.EVENTS.SESSION_LOST,
        ({ studyInstanceUid: changed }) => {
          if (changed === studyInstanceUid) {
            stopPolling();
          }
        }
      ),
    ];

    // Adopt whatever the service already knows, then read the server once. track() is
    // the only unpolled read in the milestone.
    apply();
    void diagnosisService.track(studyInstanceUid);

    return () => {
      subscriptions.forEach(subscription => subscription.unsubscribe());
      stopPolling();

      // No eviction here, deliberately. This cleanup runs on every remount, while the
      // state belongs to the mode: a read still in flight resurrects whatever is deleted
      // here, and the remount reads the gap as IDLE. Both services drop their maps in
      // onModeExit instead. develop got its eviction by overriding subscribe() on both
      // services to widen unsubscribe(uid); nothing here overrides either.
    };
  }, [diagnosisService, studyInstanceUid, refresh, startPolling, stopPolling]);

  const trigger = useCallback(() => {
    if (studyInstanceUid) {
      void diagnosisService.trigger(studyInstanceUid);
    }
  }, [diagnosisService, studyInstanceUid]);

  const revoke = useCallback(() => {
    if (studyInstanceUid) {
      void diagnosisService.revoke(studyInstanceUid);
    }
  }, [diagnosisService, studyInstanceUid]);

  // Defensive, not corrective: the one consumer (DiagnosisButton.tsx) destructures this
  // immediately, so the object's own identity is never observed today and a fresh
  // literal here would cost nothing. This guards a future second consumer that holds
  // the returned object instead of destructuring it, where a new identity every render
  // would make its useCallback pointless.
  return useMemo(() => ({ state, trigger, revoke }), [state, trigger, revoke]);
}
